import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import Form from "src/components/Form"
import InlineNotice from "src/components/InlineNotice"
import SelectPaymentMethod from "src/components/SelectPaymentMethod/SelectPaymentMethod"

import {
  cancelContactPaymentMethodSetup,
  createContactPaymentMethod,
  initializeContactPaymentMethodSetup,
} from "src/api/Manage/Contacts/PaymentMethods"
import { getPaymentMethodOptions } from "src/api/Service/WorkOrders"

const ENABLED = "true"
const DISABLED = "false"

const InvoiceAutopayFields = ({
  workOrderSequentialId,
  contactId,
  enabled,
  onEnabledChange,
  stripePaymentMethodId,
  onPaymentMethodChange,
  woDefaultEnabled,
  disabled = false,
}) => {
  const { marinaSlug } = useMarina()
  const [paymentMethods, setPaymentMethods] = useState([])

  const { data: fetched, isLoading } = useQuery({
    queryKey: ["workOrderPaymentMethods", marinaSlug, workOrderSequentialId],
    queryFn: () => getPaymentMethodOptions(marinaSlug, workOrderSequentialId),
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (fetched) setPaymentMethods(fetched)
  }, [fetched])

  const hasNoPaymentMethods = !isLoading && paymentMethods.length === 0

  const initializeSetup = () =>
    initializeContactPaymentMethodSetup({ contactId, marinaId: marinaSlug })
  const cancelSetup = (setupIntentId) =>
    cancelContactPaymentMethodSetup({
      contactId,
      marinaId: marinaSlug,
      setupIntentId,
    })
  const createPaymentMethod = (setupIntentId) =>
    createContactPaymentMethod({
      contactId,
      marinaId: marinaSlug,
      setupIntentId,
    })

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex items-center justify-between">
        <h3 className="m-0 text-sm font-semibold uppercase tracking-wide text-gray-700">
          AutoPay for this invoice
        </h3>
        <span className="text-sm text-gray-500">
          WO default:{" "}
          <span className="font-semibold">
            {woDefaultEnabled ? "Enabled" : "Disabled"}
          </span>
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <Form.Label htmlFor="invoiceAutopay">AutoPay</Form.Label>
        <Form.Select
          id="invoiceAutopay"
          value={enabled ? ENABLED : DISABLED}
          onChange={(event) => onEnabledChange(event.target.value === ENABLED)}
          disabled={disabled}
        >
          <option value={DISABLED}>Disabled — collect manually</option>
          <option value={ENABLED}>Enabled — auto-charge on due date</option>
        </Form.Select>
        <p className="text-sm text-gray-500">
          This is a per-invoice override. The WO-level default isn&rsquo;t
          changed.
        </p>
      </div>

      {enabled && (
        <>
          {hasNoPaymentMethods && (
            <InlineNotice variant="error" icon="icon-exclamation-circle">
              This contact has no payment methods. Add one below to enable
              AutoPay.
            </InlineNotice>
          )}
          <SelectPaymentMethod
            acceptedPaymentMethods={["card"]}
            paymentMethods={paymentMethods}
            setPaymentMethods={setPaymentMethods}
            paymentMethod={stripePaymentMethodId}
            setPaymentMethod={onPaymentMethodChange}
            suppressHeading
            initializeSetup={initializeSetup}
            cancelSetup={cancelSetup}
            createPaymentMethod={createPaymentMethod}
          />
        </>
      )}
    </div>
  )
}

InvoiceAutopayFields.propTypes = {
  workOrderSequentialId: PropTypes.string.isRequired,
  contactId: PropTypes.string,
  enabled: PropTypes.bool.isRequired,
  onEnabledChange: PropTypes.func.isRequired,
  stripePaymentMethodId: PropTypes.string,
  onPaymentMethodChange: PropTypes.func.isRequired,
  woDefaultEnabled: PropTypes.bool,
  disabled: PropTypes.bool,
}

export default InvoiceAutopayFields
